import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Typography,
    Alert,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Stack,
    TextField,
    Paper,
    LinearProgress,
} from "@mui/material";
import {
    Refresh,
    TrendingUp,
    TrendingDown,
    InfoOutlined,
    CalendarToday,
    Inventory,
    EventAvailable,
} from "@mui/icons-material";
import { reportsService } from "../../services/reportsService";
import { ProfitLossResponse } from "../../types/reports";
import { formatCurrencyBDT } from "../../utils/currency";

// ── Backfill defaults for fields the API may not send yet ────────────────────
// Guards against frontend/backend deploy skew (e.g. this page rolled out
// ahead of the API): render code below assumes every field is present, so if
// the API is briefly running older code that predates one of these fields,
// this fills a safe empty value instead of crashing the whole page on
// `Cannot read properties of undefined`.
const normalizeProfitLoss = (raw: any): ProfitLossResponse => ({
    ...raw,
    revenue: {
        total_gift_cost: 0,
        ...raw.revenue,
    },
    delivery_costs: {
        service_breakdown: [],
        ...raw.delivery_costs,
    },
    procurement: raw.procurement ?? {
        purchase_count: 0,
        expected_value: 0,
        expected_paid: 0,
        expected_due: 0,
    },
    inventory_valuation: raw.inventory_valuation ?? {
        total_products: 0,
        total_units: 0,
        total_cost_value: 0,
        total_retail_value: 0,
        potential_profit: 0,
        potential_margin_pct: 0,
    },
});

// ── Helper: format date for the API (ISO 8601 with time) ─────────────────────
const toApiDate = (dateStr: string, endOfDay = false): string => {
    if (!dateStr) return "";
    return endOfDay ? `${dateStr}T23:59:59` : `${dateStr}T00:00:00`;
};

// ── Helper: first/last day of current month ───────────────────────────────────
const currentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    return {
        from: `${year}-${month}-01`,
        to: `${year}-${month}-${String(lastDay).padStart(2, "0")}`,
    };
};

// ── Stat card ─────────────────────────────────────────────────────────────────
interface StatCardProps {
    label: string;
    value: number;
    subtitle?: string;
    color?: "default" | "success" | "error" | "warning" | "info";
    isCurrency?: boolean;
    isPct?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
    label,
    value,
    subtitle,
    color = "default",
    isCurrency = true,
    isPct = false,
}) => {
    const colorMap: Record<string, string> = {
        default: "text.primary",
        success: "success.main",
        error: "error.main",
        warning: "warning.main",
        info: "info.main",
    };

    const bgMap: Record<string, string> = {
        default: "background.paper",
        success: "#f0faf4",
        error: "#fff5f5",
        warning: "#fffbf0",
        info: "#f0f7ff",
    };

    const displayValue = isCurrency
        ? formatCurrencyBDT(value, { ignoreDecimals: true })
        : isPct
          ? `${Number(value).toFixed(1)}%`
          : value.toLocaleString();

    return (
        <Card variant="outlined" sx={{ bgcolor: bgMap[color], height: "100%" }}>
            <CardContent sx={{ pb: "16px !important" }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    textTransform="uppercase"
                    letterSpacing={0.5}
                >
                    {label}
                </Typography>
                <Typography
                    variant="h5"
                    fontWeight={700}
                    color={colorMap[color]}
                    mt={0.5}
                >
                    {displayValue}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

// ── P&L Section Header ────────────────────────────────────────────────────────
const SectionLabel: React.FC<{ label: string; amount: number; bold?: boolean }> = ({
    label,
    amount,
    bold = false,
}) => (
    <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={0.75}
    >
        <Typography
            variant={bold ? "subtitle1" : "body2"}
            fontWeight={bold ? 700 : 400}
            color={bold ? "text.primary" : "text.secondary"}
        >
            {label}
        </Typography>
        <Typography
            variant={bold ? "subtitle1" : "body2"}
            fontWeight={bold ? 700 : 400}
        >
            {formatCurrencyBDT(amount, { ignoreDecimals: false })}
        </Typography>
    </Box>
);

// ── Quick date preset buttons ─────────────────────────────────────────────────
type Preset = "this_month" | "last_month" | "this_year" | "all_time";

const getPresetDates = (
    preset: Preset,
): { from: string; to: string } | { from: ""; to: "" } => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    if (preset === "this_month") {
        const lastDay = new Date(y, m + 1, 0).getDate();
        return {
            from: `${y}-${String(m + 1).padStart(2, "0")}-01`,
            to: `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
        };
    }
    if (preset === "last_month") {
        const lastDay = new Date(y, m, 0).getDate();
        const lm = m === 0 ? 12 : m;
        const ly = m === 0 ? y - 1 : y;
        return {
            from: `${ly}-${String(lm).padStart(2, "0")}-01`,
            to: `${ly}-${String(lm).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
        };
    }
    if (preset === "this_year") {
        return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    return { from: "", to: "" };
};

// ── Main Component ─────────────────────────────────────────────────────────────
export const ProfitLoss: React.FC = () => {
    const defaultDates = currentMonthRange();
    const [dateFrom, setDateFrom] = useState(defaultDates.from);
    const [dateTo, setDateTo] = useState(defaultDates.to);
    const [activePreset, setActivePreset] = useState<Preset | "custom">(
        "this_month",
    );

    const [data, setData] = useState<ProfitLossResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await reportsService.getProfitLoss(
                dateFrom ? toApiDate(dateFrom) : undefined,
                dateTo ? toApiDate(dateTo, true) : undefined,
            );
            setData(normalizeProfitLoss(result));
        } catch (err: any) {
            setError(
                err?.response?.data?.detail ||
                    "Failed to load report. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePreset = (preset: Preset) => {
        setActivePreset(preset);
        const dates = getPresetDates(preset);
        setDateFrom(dates.from);
        setDateTo(dates.to);
    };

    const handleDateChange = () => {
        setActivePreset("custom");
        fetchReport();
    };

    return (
        <Box>
            {/* Page header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={3}
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Profit & Loss
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Revenue, cost of goods, and operating expenses for the
                        selected period.
                    </Typography>
                </Box>
                <Button
                    startIcon={<Refresh />}
                    variant="outlined"
                    size="small"
                    onClick={fetchReport}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Date range controls */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexWrap="wrap"
                >
                    <CalendarToday
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                    />

                    {/* Preset buttons */}
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(
                            [
                                ["this_month", "This Month"],
                                ["last_month", "Last Month"],
                                ["this_year", "This Year"],
                                ["all_time", "All Time"],
                            ] as [Preset, string][]
                        ).map(([preset, label]) => (
                            <Chip
                                key={preset}
                                label={label}
                                size="small"
                                color={
                                    activePreset === preset
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() => handlePreset(preset)}
                                sx={{ cursor: "pointer" }}
                            />
                        ))}
                    </Stack>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ display: { xs: "none", sm: "block" } }}
                    />

                    {/* Custom date pickers — on a phone Apply drops to its own line */}
                    <Stack
                        direction="row"
                        useFlexGap
                        flexWrap="wrap"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                        <TextField
                            type="date"
                            label="From"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                setActivePreset("custom");
                            }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: "auto", sm: 150 }, flex: { xs: 1, sm: "none" }, minWidth: 0 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                        <TextField
                            type="date"
                            label="To"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                setActivePreset("custom");
                            }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: "auto", sm: 150 }, flex: { xs: 1, sm: "none" }, minWidth: 0 }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleDateChange}
                            disabled={loading}
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                            Apply
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Data quality warnings */}
            {data?.data_notes?.map((note, i) => (
                <Alert
                    key={i}
                    severity="warning"
                    icon={<InfoOutlined />}
                    sx={{ mb: 2 }}
                >
                    {note}
                </Alert>
            ))}

            {data && (
                <>
                    {/* ── KPI summary row ─────────────────────────────────── */}
                    <Grid container spacing={2} mb={3}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <StatCard
                                label="Gross Revenue"
                                value={data.revenue.gross_revenue}
                                subtitle={`${data.revenue.order_count} orders`}
                                color="info"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <StatCard
                                label="Gross Profit"
                                value={data.revenue.gross_profit}
                                subtitle={`${Number(data.revenue.gross_profit_margin_pct).toFixed(1)}% margin`}
                                color={
                                    data.revenue.gross_profit >= 0
                                        ? "success"
                                        : "error"
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <StatCard
                                label="Total Expenses"
                                value={data.total_operating_expenses}
                                subtitle={`incl. ৳${Number(data.delivery_costs.total_delivery_cost).toFixed(0)} delivery`}
                                color="warning"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <StatCard
                                label="Net Profit"
                                value={data.net_profit}
                                subtitle={`${Number(data.net_profit_margin_pct).toFixed(1)}% margin`}
                                color={data.net_profit >= 0 ? "success" : "error"}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        {/* ── P&L Statement ──────────────────────────────── */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined">
                                <CardHeader
                                    title={
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                        >
                                            P&amp;L Statement
                                        </Typography>
                                    }
                                    sx={{ pb: 0 }}
                                />
                                <CardContent>
                                    {/* Revenue section */}
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                    >
                                        Revenue
                                    </Typography>
                                    <SectionLabel
                                        label="Net Revenue (after discounts)"
                                        amount={data.revenue.gross_revenue}
                                    />
                                    {/* Informational only — discounts are already netted out of
                                        the revenue figure above, not subtracted again below. */}
                                    {data.revenue.total_discounts > 0 && (
                                        <Box display="flex" justifyContent="space-between" py={0.75}>
                                            <Typography variant="caption" color="text.disabled">
                                                (of which, discounts given)
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatCurrencyBDT(data.revenue.total_discounts)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box display="flex" justifyContent="space-between" py={0.75}>
                                        <Typography variant="caption" color="text.disabled">
                                            Cash Collected
                                        </Typography>
                                        <Typography variant="caption" color={data.revenue.cash_collected < data.revenue.gross_revenue ? "warning.main" : "text.secondary"}>
                                            {formatCurrencyBDT(data.revenue.cash_collected)}
                                        </Typography>
                                    </Box>
                                    <SectionLabel
                                        label="Cost of Goods Sold (sold items)"
                                        amount={
                                            -(data.revenue.total_cogs -
                                                data.revenue.total_gift_cost)
                                        }
                                    />
                                    {data.revenue.total_gift_cost > 0 && (
                                        <SectionLabel
                                            label="Cost of Gifts Given"
                                            amount={-data.revenue.total_gift_cost}
                                        />
                                    )}
                                    <Divider sx={{ my: 1 }} />
                                    <SectionLabel
                                        label="Gross Profit"
                                        amount={data.revenue.gross_profit}
                                        bold
                                    />
                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        mb={1.5}
                                    >
                                        <Chip
                                            label={`${Number(data.revenue.gross_profit_margin_pct).toFixed(1)}% margin`}
                                            size="small"
                                            color={
                                                data.revenue.gross_profit >= 0
                                                    ? "success"
                                                    : "error"
                                            }
                                            variant="outlined"
                                            icon={
                                                data.revenue.gross_profit >=
                                                0 ? (
                                                    <TrendingUp
                                                        fontSize="small"
                                                    />
                                                ) : (
                                                    <TrendingDown
                                                        fontSize="small"
                                                    />
                                                )
                                            }
                                        />
                                    </Box>

                                    {/* Operating expenses section */}
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                        mt={1}
                                    >
                                        Operating Expenses
                                    </Typography>
                                    <SectionLabel
                                        label="Delivery Costs"
                                        amount={
                                            -data.delivery_costs
                                                .total_delivery_cost
                                        }
                                    />
                                    {data.operating_expenses.map((exp) => (
                                        <SectionLabel
                                            key={exp.category_name}
                                            label={exp.category_name}
                                            amount={-exp.total_amount}
                                        />
                                    ))}
                                    {data.operating_expenses.length === 0 &&
                                        data.delivery_costs
                                            .total_delivery_cost === 0 && (
                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                                display="block"
                                                py={1}
                                            >
                                                No expenses recorded for this
                                                period.
                                            </Typography>
                                        )}
                                    <Divider sx={{ my: 1 }} />
                                    <SectionLabel
                                        label="Total Operating Expenses"
                                        amount={-data.total_operating_expenses}
                                        bold
                                    />
                                    <Divider sx={{ my: 1.5 }} />
                                    <SectionLabel
                                        label="EBITDA"
                                        amount={data.ebitda}
                                        bold
                                    />
                                    <Box display="flex" justifyContent="flex-end" mb={1}>
                                        <Chip
                                            label={`${Number(data.ebitda_margin_pct).toFixed(1)}% margin`}
                                            size="small"
                                            color={data.ebitda >= 0 ? "success" : "error"}
                                            variant="outlined"
                                            icon={data.ebitda >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                                        />
                                    </Box>

                                    {/* Depreciation section */}
                                    <Typography variant="overline" color="text.secondary" display="block" mb={0.5} mt={1}>
                                        Depreciation &amp; Amortization
                                    </Typography>
                                    {data.depreciation.asset_count === 0 ? (
                                        <Typography variant="caption" color="text.disabled" display="block" py={0.5}>
                                            No assets recorded yet. Add assets to the Asset Register to see depreciation here.
                                        </Typography>
                                    ) : (
                                        <SectionLabel
                                            label={`Depreciation (${data.depreciation.asset_count} asset${data.depreciation.asset_count !== 1 ? "s" : ""})`}
                                            amount={-data.depreciation.total_depreciation}
                                        />
                                    )}
                                    <Divider sx={{ my: 1.5 }} />
                                    <SectionLabel
                                        label="Net Profit (EBIT)"
                                        amount={data.net_profit}
                                        bold
                                    />
                                    <Box display="flex" justifyContent="flex-end">
                                        <Chip
                                            label={`${Number(data.net_profit_margin_pct).toFixed(1)}% margin`}
                                            size="small"
                                            color={data.net_profit >= 0 ? "success" : "error"}
                                            variant="outlined"
                                            icon={data.net_profit >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                                        />
                                    </Box>

                                    {/* Delivery margin note */}
                                    {data.delivery_costs.order_count_with_delivery > 0 && (
                                        <Box mt={1.5} p={1.5} bgcolor={data.delivery_costs.delivery_margin >= 0 ? "#f0faf4" : "#fff5f5"} borderRadius={1}>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5} fontWeight={600}>
                                                Delivery Margin
                                            </Typography>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="caption" color="text.secondary">Shipping charged to customers</Typography>
                                                <Typography variant="caption">{formatCurrencyBDT(data.delivery_costs.total_shipping_revenue)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="caption" color="text.secondary">Delivery cost paid</Typography>
                                                <Typography variant="caption">−{formatCurrencyBDT(data.delivery_costs.total_delivery_cost)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mt={0.5}>
                                                <Typography variant="caption" fontWeight={700}>Net delivery margin</Typography>
                                                <Typography variant="caption" fontWeight={700} color={data.delivery_costs.delivery_margin >= 0 ? "success.main" : "error.main"}>
                                                    {formatCurrencyBDT(data.delivery_costs.delivery_margin)}
                                                </Typography>
                                            </Box>
                                            {data.delivery_costs.service_breakdown.length >
                                                1 && (
                                                <Box mt={1} pt={1} borderTop="1px solid" borderColor="divider">
                                                    {data.delivery_costs.service_breakdown.map(
                                                        (svc) => (
                                                            <Box
                                                                key={svc.name}
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                py={0.25}
                                                            >
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {svc.name}
                                                                    {svc.type ? ` (${svc.type})` : ""}
                                                                    {" · "}
                                                                    {svc.count} orders
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight={600}
                                                                    color={svc.profit >= 0 ? "success.main" : "error.main"}
                                                                >
                                                                    {formatCurrencyBDT(svc.profit)}
                                                                </Typography>
                                                            </Box>
                                                        ),
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Footnote */}
                                    <Box
                                        mt={2}
                                        p={1.5}
                                        bgcolor="grey.50"
                                        borderRadius={1}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Revenue includes orders with status:{" "}
                                            {data.revenue.included_statuses.join(
                                                ", ",
                                            )}
                                            . CANCELLED and PENDING orders are
                                            excluded.
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* ── Expense breakdown table ─────────────────────── */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardHeader
                                    title={
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                        >
                                            Expense Breakdown by Category
                                        </Typography>
                                    }
                                    sx={{ pb: 0 }}
                                />
                                <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                                    {data.operating_expenses.length === 0 ? (
                                        <Box p={3}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                No expenses recorded for this
                                                period. Add expenses via the
                                                Expenses section.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Category
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Entries
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Amount
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.operating_expenses.map(
                                                    (exp) => (
                                                        <TableRow
                                                            key={
                                                                exp.category_name
                                                            }
                                                        >
                                                            <TableCell>
                                                                {
                                                                    exp.category_name
                                                                }
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {
                                                                    exp.transaction_count
                                                                }
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyBDT(
                                                                    exp.total_amount,
                                                                    {
                                                                        ignoreDecimals:
                                                                            true,
                                                                    },
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                                <TableRow
                                                    sx={{
                                                        "& td": {
                                                            fontWeight: 700,
                                                            borderTop:
                                                                "2px solid",
                                                            borderColor:
                                                                "divider",
                                                        },
                                                    }}
                                                >
                                                    <TableCell>Total</TableCell>
                                                    <TableCell align="right">
                                                        {data.operating_expenses.reduce(
                                                            (s, e) =>
                                                                s +
                                                                e.transaction_count,
                                                            0,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrencyBDT(
                                                            data.operating_expenses.reduce(
                                                                (s, e) =>
                                                                    s +
                                                                    e.total_amount,
                                                                0,
                                                            ),
                                                            { ignoreDecimals: true },
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>

                            {/* ── Revenue composition card ──────────────────── */}
                            <Card variant="outlined">
                                <CardHeader
                                    title={
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                        >
                                            Revenue Composition
                                        </Typography>
                                    }
                                    sx={{ pb: 1 }}
                                />
                                <CardContent sx={{ pt: 0 }}>
                                    {[
                                        {
                                            label: "COGS",
                                            value: data.revenue.total_cogs,
                                            color: "#ef5350",
                                        },
                                        {
                                            label: "Delivery Costs",
                                            value: data.delivery_costs
                                                .total_delivery_cost,
                                            color: "#ff9800",
                                        },
                                        {
                                            label: "Other Expenses",
                                            value: data.operating_expenses.reduce(
                                                (s, e) => s + e.total_amount,
                                                0,
                                            ),
                                            color: "#ffc107",
                                        },
                                        {
                                            label: "Operating Profit",
                                            value: Math.max(
                                                0,
                                                data.operating_profit,
                                            ),
                                            color: "#4caf50",
                                        },
                                    ].map((seg) => {
                                        const pct =
                                            data.revenue.gross_revenue > 0
                                                ? (seg.value /
                                                      data.revenue
                                                          .gross_revenue) *
                                                  100
                                                : 0;
                                        return (
                                            <Box key={seg.label} mb={1.5}>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    mb={0.25}
                                                >
                                                    <Typography variant="caption">
                                                        {seg.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                    >
                                                        {pct.toFixed(1)}% —{" "}
                                                        {formatCurrencyBDT(
                                                            seg.value,
                                                            {
                                                                ignoreDecimals:
                                                                    true,
                                                            },
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(100, pct)}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 4,
                                                        bgcolor: "grey.100",
                                                        "& .MuiLinearProgress-bar":
                                                            {
                                                                bgcolor:
                                                                    seg.color,
                                                                borderRadius: 4,
                                                            },
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* ── Supplier Payments (cash flow to suppliers) ───────── */}
                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardHeader
                            title={
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Supplier Payments (Cash Paid for Inventory)
                                </Typography>
                            }
                            subheader="Cash actually paid to suppliers during this period — recorded when payment is made, not when goods are purchased."
                            sx={{ pb: 1 }}
                        />
                        <CardContent>
                            {data.supplier_payments.payment_count === 0 ? (
                                <Alert severity="info" variant="outlined">
                                    No supplier payments recorded for this period. Use the Procurement → View Purchase Order page to record payments made to suppliers.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <StatCard
                                            label="Cash Paid to Suppliers"
                                            value={data.supplier_payments.total_paid}
                                            subtitle={`${data.supplier_payments.payment_count} payments across ${data.supplier_payments.purchase_count} orders`}
                                            color="warning"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 8 }}>
                                        <Box p={2} bgcolor="grey.50" borderRadius={1} height="100%" display="flex" flexDirection="column" justifyContent="center">
                                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                                Note: This is <strong>cash basis</strong> — actual money paid out. The COGS above is <strong>accrual basis</strong> — cost of goods that were sold. These will differ if you paid suppliers before selling the goods, or sold goods before paying suppliers.
                                            </Typography>
                                            <Box display="flex" justifyContent="space-between" mt={1}>
                                                <Typography variant="body2">Cash paid to suppliers</Typography>
                                                <Typography variant="body2" fontWeight={700} color="error.main">
                                                    −{formatCurrencyBDT(data.supplier_payments.total_paid, { ignoreDecimals: true })}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Cash collected from customers</Typography>
                                                <Typography variant="body2" fontWeight={700} color="success.main">
                                                    +{formatCurrencyBDT(data.revenue.cash_collected, { ignoreDecimals: true })}
                                                </Typography>
                                            </Box>
                                            <Divider sx={{ my: 1 }} />
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2" fontWeight={700}>Net cash (inventory only)</Typography>
                                                <Typography variant="body2" fontWeight={700} color={data.revenue.cash_collected - data.supplier_payments.total_paid >= 0 ? "success.main" : "error.main"}>
                                                    {formatCurrencyBDT(data.revenue.cash_collected - data.supplier_payments.total_paid, { ignoreDecimals: true })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>

                    {/* ── Procurement due this period (by expected delivery) ─ */}
                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardHeader
                            title={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <EventAvailable
                                        fontSize="small"
                                        color="action"
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                    >
                                        Procurement Due This Period
                                    </Typography>
                                </Box>
                            }
                            subheader="Purchase orders by expected delivery date, not when the PO was created or paid — a different cut from Supplier Payments above."
                            sx={{ pb: 1 }}
                        />
                        <CardContent>
                            {data.procurement.purchase_count === 0 ? (
                                <Alert severity="info" variant="outlined">
                                    No purchase orders have an expected
                                    delivery date within this period.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Purchase Orders"
                                            value={
                                                data.procurement
                                                    .purchase_count
                                            }
                                            subtitle="expected this period"
                                            isCurrency={false}
                                            color="info"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Expected Value"
                                            value={
                                                data.procurement
                                                    .expected_value
                                            }
                                            subtitle="total committed"
                                            color="info"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Paid So Far"
                                            value={
                                                data.procurement
                                                    .expected_paid
                                            }
                                            subtitle="against these POs"
                                            color="success"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Still Due"
                                            value={
                                                data.procurement.expected_due
                                            }
                                            subtitle="unpaid so far"
                                            color={
                                                data.procurement
                                                    .expected_due > 0
                                                    ? "warning"
                                                    : "success"
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>

                    {/* ── Inventory Valuation (current stock snapshot) ─────── */}
                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardHeader
                            title={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Inventory fontSize="small" color="action" />
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                    >
                                        Inventory Valuation
                                    </Typography>
                                </Box>
                            }
                            subheader="Stock on hand right now — not affected by the date range above."
                            sx={{ pb: 1 }}
                        />
                        <CardContent>
                            {data.inventory_valuation.total_products === 0 ? (
                                <Alert severity="info" variant="outlined">
                                    No products currently have stock on hand.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Products in Stock"
                                            value={
                                                data.inventory_valuation
                                                    .total_products
                                            }
                                            subtitle={`${data.inventory_valuation.total_units.toLocaleString()} units total`}
                                            isCurrency={false}
                                            color="info"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Stock Value (Cost)"
                                            value={
                                                data.inventory_valuation
                                                    .total_cost_value
                                            }
                                            subtitle="What this stock cost us"
                                            color="warning"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Stock Value (Retail)"
                                            value={
                                                data.inventory_valuation
                                                    .total_retail_value
                                            }
                                            subtitle="If sold at current prices"
                                            color="info"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <StatCard
                                            label="Potential Profit"
                                            value={
                                                data.inventory_valuation
                                                    .potential_profit
                                            }
                                            subtitle={`${Number(data.inventory_valuation.potential_margin_pct).toFixed(1)}% margin if all sold`}
                                            color={
                                                data.inventory_valuation
                                                    .potential_profit >= 0
                                                    ? "success"
                                                    : "error"
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            {!data && !loading && !error && (
                <Box textAlign="center" py={8}>
                    <Typography color="text.secondary">
                        Select a date range and click Apply to generate the
                        report.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
